import { Box, Stack, Text, useMantineTheme } from '@mantine/core';
import { Bar, ComposedChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { CardShell } from '../../atlas/cards/CardShell';
import { MetricRow } from '../../atlas/cards/MetricRow';
import { countYAxisReferenceGridLines } from '../../countYAxisReferenceGridLines';
import { renderChartGoalLines } from '../../ChartGoalLines';
import {
  aggregateQuarterlyChartToAnnual,
  attachMappedGoalTargets,
  collectGoalValues,
  latest,
  parseQuarter,
  pctChange,
  previous,
  buildCountYAxisScaleForChart,
} from '../componentProjectMetricUtils';
import type { SafeTComponentProjectData } from './mapSafeTComponentProjectData';

type SafeTFacilityUtilizationCardProps = {
  data: SafeTComponentProjectData;
};

function formatQoQ(qoq: number | null): string {
  if (qoq === null) return '—';
  if (qoq === 0) return '0.00% QoQ';
  const arrow = qoq > 0 ? '↑' : '↓';
  return `${arrow}${Math.abs(qoq).toFixed(2)}% QoQ`;
}

function formatHours(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  const unit = value === 1 ? 'hour' : 'hours';
  return `${value} ${unit}`;
}

export function SafeTFacilityUtilizationCard({ data }: SafeTFacilityUtilizationCardProps) {
  const theme = useMantineTheme();
  const series = data.series;
  const cur = latest(series);
  const prev = previous(series);
  const subtitle = cur?.quarter ?? 'No data';
  const qoq = cur ? pctChange(cur.facilityUtilization, prev?.facilityUtilization ?? null) : null;

  const currentMeta = parseQuarter(cur?.quarter ?? '');
  const ytd = currentMeta
    ? series
        .filter((point) => {
          const meta = parseQuarter(point.quarter);
          return meta?.year === currentMeta.year && meta.quarter <= currentMeta.quarter;
        })
        .reduce((sum, point) => sum + point.facilityUtilization, 0)
    : 0;

  const yoy = (() => {
    if (!currentMeta) return null;
    const prevYearTotal = series
      .filter((point) => {
        const meta = parseQuarter(point.quarter);
        return meta?.year === currentMeta.year - 1 && meta.quarter <= currentMeta.quarter;
      })
      .reduce((sum, point) => sum + point.facilityUtilization, 0);
    return pctChange(ytd, prevYearTotal);
  })();
  const arrow = (n: number): '↑' | '↓' | '' => (n > 0 ? '↑' : n < 0 ? '↓' : '');
  const yoyText =
    yoy === null ? '— YoY' : yoy === 0 ? '0.00% YoY' : `${arrow(yoy)}${Math.abs(yoy).toFixed(2)}% YoY`;

  const quarterlyChartData = attachMappedGoalTargets(
    series.map((point) => ({
      quarter: point.quarter,
      facilityUtilization: point.facilityUtilization,
    })),
    series,
    [{ dataKey: 'facilityUtilization', goalField: 'facilityUtilization' }]
  );
  const chartData = aggregateQuarterlyChartToAnnual(quarterlyChartData, ['facilityUtilization']);

  const hasData =
    series.some((point) => point.facilityUtilization > 0) || data.allTime.facilityUtilization > 0;

  const goalValues = collectGoalValues(chartData, ['facilityUtilization']);
  const chartMax = Math.max(
    0,
    ...chartData.map((point) => Number(point.facilityUtilization))
  );
  const { yAxisMax, yAxisTicks } = buildCountYAxisScaleForChart([chartMax], goalValues);
  const gridLineStroke =
    theme.colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.12)';

  const tooltipShellStyle = {
    backgroundColor: theme.colorScheme === 'dark' ? '#000' : '#fff',
    border: `1px solid ${theme.colorScheme === 'dark' ? '#333' : '#ddd'}`,
    borderRadius: 10,
    color: theme.colorScheme === 'dark' ? '#fff' : '#000',
    padding: '10px 12px',
    maxWidth: 320,
    whiteSpace: 'normal' as const,
    wordBreak: 'break-word' as const,
  } as const;

  return (
    <CardShell title="Facility Utilization Hours" subtitle={subtitle}>
      <Stack spacing="sm">
        <MetricRow
          items={[
            {
              label: 'This Quarter',
              labelTooltip: 'Facility utilization hours reported this quarter.',
              value: (
                <Text size="lg" weight={800}>
                  {cur?.facilityUtilization ?? '—'}
                </Text>
              ),
              sublabel: formatQoQ(qoq),
              reserveGoalSublabel: true,
            },
            {
              label: 'YTD',
              labelTooltip: 'Calendar year to date facility utilization hours.',
              value: (
                <Text size="lg" weight={800}>
                  {ytd}
                </Text>
              ),
              sublabel: yoyText,
              reserveGoalSublabel: true,
            },
            {
              label: 'All-Time',
              labelTooltip: 'Total facility utilization hours across all reported quarters.',
              value: (
                <Text size="lg" weight={800}>
                  {data.allTime.facilityUtilization}
                </Text>
              ),
            },
          ]}
        />

        <Text size="xs" color="dimmed" weight={700} sx={{ letterSpacing: 0.2, marginTop: '0.75rem' }}>
          Facility Utilization Hours by Year
        </Text>

        <Box sx={{ height: 180 }}>
          {hasData ? (
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartData} barCategoryGap="12%">
                <XAxis
                  dataKey="yearLabel"
                  tick={{ fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                  interval="preserveStartEnd"
                  minTickGap={12}
                />
                <YAxis
                  orientation="right"
                  tick={{ fontSize: 11 }}
                  allowDecimals={false}
                  axisLine={false}
                  tickLine={false}
                  width={44}
                  tickMargin={8}
                  domain={[0, yAxisMax]}
                  ticks={yAxisTicks}
                />
                {countYAxisReferenceGridLines(yAxisTicks, gridLineStroke, goalValues)}
                {renderChartGoalLines(chartData, ['facilityUtilization'])}
                <Tooltip
                  cursor={false}
                  content={({ active, payload, label }) => {
                    if (!active || !payload || payload.length === 0) return null;
                    const value = payload[0]?.value;
                    return (
                      <div style={tooltipShellStyle}>
                        <div style={{ fontWeight: 700, marginBottom: 6, fontSize: 12, opacity: 0.9 }}>
                          {label}
                        </div>
                        <div style={{ fontWeight: 900, fontSize: 14, lineHeight: 1.3 }}>
                          {typeof value === 'number' ? formatHours(value) : '—'}
                        </div>
                      </div>
                    );
                  }}
                />
                <Bar
                  dataKey="facilityUtilization"
                  fill="#FAB005"
                  radius={[6, 6, 0, 0]}
                  barSize={18}
                  isAnimationActive={false}
                  activeBar={false}
                />
              </ComposedChart>
            </ResponsiveContainer>
          ) : (
            <Text size="sm" color="dimmed" sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
              No facility utilization data reported yet.
            </Text>
          )}
        </Box>
      </Stack>
    </CardShell>
  );
}
